import {
  Accordion,
  Badge,
  BadgeVariants,
  ButtonText,
  Checkbox,
  useToast
} from '@morressier/alembic';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBell,
  faComment,
  faPencil
} from '@fortawesome/pro-regular-svg-icons';
import { useEffect, useState } from 'react';
import { Flex, Text } from '@chakra-ui/react';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { useForm } from 'react-hook-form';
import { useQueryClient, useQuery } from '@tanstack/react-query';

import { Utils } from '@publishing-engine/editorial-form-field-build/src';
import {
  IEditorialFormField,
  ISubmissionFile
} from '@publishing-engine/domain/models';
import ProtectedAction from 'src/components/ProtectedAction';
import { ERRORS } from 'src/constants';
import { ExtendSubmissionDeadlineModal } from '../../../decisionModals/GrantSubmissionExtensionModal';
import { EditorialRevisionContextModal } from '../../../modals/editorial/RevisonContext';
import { useSubmissionData } from 'src/contexts/SubmissionDataProvider';
import { useUpdateEditorialFormData } from 'src/api/commands';
import useEditorialForm from 'src/hooks/useEditorialForm';
import { OverwriteReviewsModal } from '../common/OverwriteReviewsModal';
import {
  fetchSubmissionReviews,
  fetchSubmissionReviewsSummary
} from 'src/api/queries';
import { AuthorCommentsModal } from '../common/AuthorCommentsModal';
import {
  CompletedReviews,
  CriticalDeadlineReviews,
  ReviewsDeadlineRequested
} from '../common/HighlightsComponents';
import { EditorialAuthorWithdrawalRequested } from '../common/WithdrawalRequested';
import AdditionalInformationWrapper from '../../AdditionalInformationWrapper';
import { getCheckListName } from 'src/utils/string';
import { FieldLabel } from 'src/components/Field';

const IconBadge = ({
  icon,
  variant
}: {
  icon: IconProp;
  variant: BadgeVariants;
}) => (
  <Badge size="context" variant={variant}>
    <Flex
      width="24px"
      height={'24px'}
      alignItems={'center'}
      justifyContent={'center'}
    >
      <FontAwesomeIcon icon={icon} />
    </Flex>
  </Badge>
);

export const EditorialRevisionAssessmentAdditionalInfo = () => {
  const toast = useToast();
  const {
    submissionId,
    submissionContextId,
    workflowResponse,
    fieldsResponse: { fields, organizationId }
  } = useSubmissionData();
  const { articleWorkflow } = workflowResponse;
  const submissionWorkflowState =
    workflowResponse.workflowData.currentState;

  const deadline = workflowResponse.workflowData.currentDeadline;
  const [showReviewContextModal, setShowReviewContextModal] = useState(false);
  const [showGrantExtensionModal, setShowGrantExtensionModal] = useState(false);
  const [showOverwriteModal, setShowOverwriteModal] = useState(false);
  const [showAuthorCommentsModal, setShowAuthorCommentsModal] = useState(false);

  const isExtensionRequested =
    workflowResponse.workflowData.deadlineExtensions?.status === 'requested';
  const fetchRevisionAssessmentDataKey = [
    'submission-checklist',
    {
      submissionId,
      phase: 'revisionAssessment',
      articleWorkflowId: articleWorkflow.id
    }
  ];

  const updateChecklistMutation = useUpdateEditorialFormData(
    submissionId,
    submissionContextId,
    articleWorkflow.id,
    'revisionAssessment',
    fetchRevisionAssessmentDataKey,
    organizationId
  );
  const {
    editorialFormSchema: revisionAssessmentFormSchema,
    editorialFormData: revisionAssessmentData,
    defaultFormValues
  } = useEditorialForm(
    submissionId,
    submissionContextId,
    articleWorkflow.organizationId,
    'revisionAssessment',
    articleWorkflow.id
  );

  const { reset, register } = useForm<Record<string, boolean>>({
    defaultValues: defaultFormValues
  });

  const authorResponse = fields.AuthorResponse?.value as string;
  const authorAttachments = fields.AuthorResponseAttachments
    ?.value as ISubmissionFile[];

  useEffect(() => {
    if (revisionAssessmentData?.caseData) {
      reset({
        ...defaultFormValues,
        ...(revisionAssessmentData?.caseData as Record<string, boolean>)
      });
    }
  }, [revisionAssessmentData, reset]);

  const updateChecklist = async (name: string, value: boolean) => {
    const response = await updateChecklistMutation.mutateAsync({
      [name]: value
    });
    const toastFieldName = getCheckListName(name);
    if (response.isSuccess) {
      return toast({
        variant: 'success',
        title: `${toastFieldName} ${value ? 'Checked' : 'Unchecked'}`
      });
    } else {
      return toast({
        variant: 'error',
        title: ERRORS.somethingWentWrong
      });
    }
  };

  const renderCheckbox = (field: IEditorialFormField) => {
    const { name, component, params } = field;
    const { caseDataField, labels, validations } = params;

    const registerField = register(name);

    return (
      <Flex key={name} gap={2} flexDirection="column">
        <ProtectedAction
          relation="task_completer"
          entityType="journal_phase"
          entityId={`${submissionContextId}_${submissionWorkflowState}`}
          tooltipLabel="You do not have permission to perform this action"
        >
          {component === 'Checkbox' ? (
            <Checkbox
              id={name}
              ariaLabel={labels.main}
              {...registerField}
              onChange={e => {
                registerField.onChange(e);
                updateChecklist(name, e.target.checked);
              }}
              value={
                Utils.JsonUtil.extractByPath({
                  json: revisionAssessmentData,
                  path: caseDataField
                }) as string
              }
              isChecked={Boolean(
                Utils.JsonUtil.extractByPath({
                  json: revisionAssessmentData,
                  path: caseDataField
                })
              )}
              isRequired={validations?.required}
            >
              <FieldLabel
                isRequired={validations?.required}
                label={labels.main}
                subText={''}
                helpText={labels.description}
              />
            </Checkbox>
          ) : (
            <></>
          )}
        </ProtectedAction>
      </Flex>
    );
  };

  const queryClient = useQueryClient();

  const { data: reviewsData } = useQuery({
    queryKey: ['review-list', { submissionContextId, submissionId }],
    queryFn: () => fetchSubmissionReviews(submissionContextId, submissionId)
  });

  const { data: reviewsSummary } = useQuery({
    queryKey: ['summary-count', { submissionContextId, submissionId }],
    queryFn: () =>
      fetchSubmissionReviewsSummary(submissionContextId, submissionId)
  });

  const completedReviews = reviewsData?.reviewSummaryCounts?.completed || 0;

  const criticalDeadlineReviews =
    reviewsSummary?.highlightsCounts?.approachingDeadline || 0;

  const expiredReviews = reviewsSummary?.highlightsCounts?.expired || 0;

  const deadlineExtensionRequests =
    reviewsData?.reviewSummaryCounts?.extensionRequested || 0;

  const isWithdrawalRequested =
    !!workflowResponse.workflowData.withdrawal?.requested;
  const hasHighlights =
    authorResponse ||
    criticalDeadlineReviews > 0 ||
    expiredReviews > 0 ||
    deadlineExtensionRequests > 0 ||
    isWithdrawalRequested;

  return (
    <AdditionalInformationWrapper>
      {showAuthorCommentsModal && (
        <AuthorCommentsModal
          onClose={() => setShowAuthorCommentsModal(false)}
          authorResponse={authorResponse}
          authorAttachments={authorAttachments}
          submissionContextId={submissionContextId}
        />
      )}

      {showOverwriteModal && (
        <OverwriteReviewsModal
          handleCloseModal={() => setShowOverwriteModal(false)}
          submissionId={submissionId}
          submissionContextId={submissionContextId}
          organizationId={organizationId}
          refetchSubmission={() => {
            queryClient.invalidateQueries(fetchRevisionAssessmentDataKey);
          }}
          defaultMinReviews={workflowResponse.workflowData.minReviews}
        />
      )}

      <Flex flexDirection="column">
        {/* ----- Highlights ----- */}
        {hasHighlights && (
          <Accordion
            chakraAccordionProps={{
              width: '100%',
              defaultIndex: 0,
              allowToggle: true
            }}
            items={[
              {
                title: (
                  <Flex alignItems={'flex-start'} gap={2}>
                    <IconBadge icon={faBell} variant="neutral" />
                    <Flex flexDirection={'column'} marginLeft={2}>
                      <Text textStyle="headingItem">Highlights</Text>
                      <Text textStyle="textContext">
                        Please review key updates and other relevant information
                        in this phase.
                      </Text>
                    </Flex>
                  </Flex>
                ),
                content: (
                  <Flex flexDirection="column" gap={2}>
                    <EditorialAuthorWithdrawalRequested />
                    {authorResponse && (
                      <>
                        <Flex
                          width="100%"
                          gap={3}
                          alignItems="center"
                          paddingTop={2}
                          paddingBottom={4}
                          borderColor="neutralPrimaryBorder"
                          borderBottomWidth={1}
                          borderStyle="solid"
                        >
                          <IconBadge icon={faComment} variant="attention" />

                          <Flex flexDirection="column" flex={1}>
                            <Flex>
                              <Text textStyle="headingItem">
                                Author Comments Available
                              </Text>
                              <ButtonText
                                marginLeft={'auto'}
                                onClick={() => setShowAuthorCommentsModal(true)}
                              >
                                View Comments
                              </ButtonText>
                            </Flex>
                          </Flex>
                        </Flex>
                      </>
                    )}

                    <CompletedReviews
                      minReviews={workflowResponse.workflowData.minReviews}
                      completedReviews={completedReviews}
                    />

                    <CriticalDeadlineReviews
                      criticalDeadlineReviews={criticalDeadlineReviews}
                      expiredReviews={expiredReviews}
                    />

                    <ReviewsDeadlineRequested
                      deadlineExtensionRequests={deadlineExtensionRequests}
                    />
                  </Flex>
                )
              }
            ]}
          />
        )}

        {/* ----- Task Checklist ----- */}
        {(revisionAssessmentFormSchema?.formFields || []).length > 0 && (
          <Accordion
            chakraAccordionProps={{
              width: '100%',
              defaultIndex: 0,
              allowToggle: true
            }}
            items={[
              {
                title: (
                  <Flex alignItems={'flex-start'} gap={2}>
                    <IconBadge icon={faPencil} variant="neutral" />
                    <Flex flexDirection={'column'} marginLeft={2}>
                      <Text textStyle="headingItem">Task Checklist</Text>
                      <Text textStyle="textContext">
                        Ensure all required steps are completed before advancing
                        in the submission process.
                      </Text>
                    </Flex>
                  </Flex>
                ),
                content: (
                  <Flex flexDirection="column" gap={2}>
                    <form>
                      <Flex gap={4} flexDirection="column">
                        {revisionAssessmentFormSchema?.formFields?.map(
                          (field: IEditorialFormField) => renderCheckbox(field)
                        )}
                      </Flex>
                    </form>
                  </Flex>
                )
              }
            ]}
          />
        )}
      </Flex>

      {showReviewContextModal && (
        <EditorialRevisionContextModal
          submissionId={submissionId}
          onClose={() => setShowReviewContextModal(false)}
          deadline={deadline}
          role="editorial"
        />
      )}

      {showGrantExtensionModal && (
        <ExtendSubmissionDeadlineModal
          onClose={() => setShowGrantExtensionModal(false)}
          submissionId={submissionId}
          organizationId={organizationId}
          submissionContextId={submissionContextId}
          isExtensionRequested={isExtensionRequested}
          proposedDate={
            workflowResponse.workflowData.deadlineExtensions?.proposedDate
          }
        />
      )}
    </AdditionalInformationWrapper>
  );
};
